import type { KeyboardEvent } from 'react';
import { useRef } from 'react';
import { Button, Form } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import type { ClientContactType } from '@econtractor/types';
import ESpinner from 'components/others/Spinner';
import SolarIcon from 'components/icons/SolarIcon';
import SvgTrash from 'components/svg/SvgTrash';
import { useModal } from 'context/ModalContext';
import { getButtonsNoYesDanger } from 'context/helpers/helperModalContext';
import ContactValueField from './ContactValueField';
import ClientContactsTable from './ClientContactsTable';
import {
    CLIENT_CONTACT_TYPE_CONFIG,
    CLIENT_CONTACT_TYPE_LIST,
    type ClientContactRow,
    type ContactsAdapter,
} from './model/clientContactTypes';
import useClientContacts, { type ClientContactsMode } from './model/useClientContacts';

interface ClientContactsProps {
    clientId?: number | null;
    ownerId?: number | null;
    adapter?: ContactsAdapter;
    mode: ClientContactsMode;
    drafts?: ClientContactRow[];
    onDraftsChange?: (next: ClientContactRow[]) => void;
}

const ClientContacts = ({ clientId, ownerId, adapter, mode, drafts, onDraftsChange }: ClientContactsProps) => {
    const { t } = useTranslation(['Clients', 'common']);
    const { showDlg } = useModal();
    const {
        contacts,
        loading,
        saving,
        canEdit,
        editingKey,
        editValue,
        newType,
        newValue,
        setNewType,
        setNewValue,
        setEditValue,
        addContact,
        startEdit,
        cancelEdit,
        saveEdit,
        removeContact,
    } = useClientContacts({ clientId, ownerId, adapter, mode, drafts, onDraftsChange });
    const InputValueRef = useRef<HTMLInputElement>(null);
    const newTypeConfig = CLIENT_CONTACT_TYPE_CONFIG[newType];

    const handleEditKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
        if (event.key === 'Enter') {
            event.preventDefault();
            void saveEdit();
        }
        if (event.key === 'Escape') cancelEdit();
    };

    const handleRemove = (row: ClientContactRow) => {
        const buttons = [...getButtonsNoYesDanger(t)];
        buttons[1].handleClick = () => {
            showDlg(false);
            void removeContact(row);
        };

        showDlg({
            type: 'confirm',
            buttons,
            text: (
                <>
                    <h4 className="d-flex pb-2 mb-2 border-bottom text-danger">
                        <SvgTrash className="text-danger" />
                        <span className="ps-2">{t('contacts.confirmDeleteTitle')}</span>
                    </h4>
                    <h5 className="mb-0">{row.contactValue}</h5>
                    <small className="form-text text-muted">{t('contacts.confirmDeleteInfo')}</small>
                </>
            ),
        });
    };

    if (loading && !contacts.length) {
        return <ESpinner />;
    }

    return (
        <div className="position-relative">
            <p className="text-muted mb-3">{t('contacts.subtitle')}</p>

            <ClientContactsTable
                rows={contacts}
                showSpinner={saving}
                canEdit={canEdit}
                editingKey={editingKey}
                editValue={editValue}
                onEditValueChange={setEditValue}
                onEditKeyDown={handleEditKeyDown}
                onStartEdit={startEdit}
                onSaveEdit={() => void saveEdit()}
                onCancelEdit={cancelEdit}
                onRemove={handleRemove}
            />

            {canEdit && (
                <div className="border rounded p-3 mt-3">
                    <div className="fw-medium mb-3">{t('contacts.addTitle')}</div>
                    <Form.Group className="mb-2">
                        <Form.Label className="text-muted">{t('contacts.columns.type')}</Form.Label>
                        <Form.Select
                            value={newType}
                            disabled={saving}
                            onChange={(e) => {
                                setNewType(e.target.value as ClientContactType);
                                setNewValue('');
                                InputValueRef.current?.focus();
                            }}
                        >
                            {CLIENT_CONTACT_TYPE_LIST.map(({ type }) => (
                                <option key={type} value={type}>
                                    {t(`contacts.types.${type}`)}
                                </option>
                            ))}
                        </Form.Select>
                    </Form.Group>
                    <Form.Group className="mb-3">
                        <Form.Label className="text-muted">{t('contacts.columns.value')}</Form.Label>
                        <ContactValueField
                            inputRef={InputValueRef}
                            type={newType}
                            disabled={saving}
                            value={newValue}
                            placeholder={t(`contacts.placeholders.${newTypeConfig.placeholderKey}`)}
                            onChange={setNewValue}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter') {
                                    e.preventDefault();
                                    void addContact();
                                }
                            }}
                        />
                    </Form.Group>
                    <div className="d-grid">
                        <Button variant="primary" disabled={saving} onClick={() => void addContact()}>
                            <SolarIcon name="plus" className="me-1 fs-14" />
                            {t('contacts.addButton')}
                        </Button>
                    </div>
                </div>
            )}
        </div>
    );
};

export type { ClientContactRow, ContactsAdapter };
export default ClientContacts;
